import React, { useEffect, useRef, useImperativeHandle, forwardRef } from 'react'

import length from '@turf/length'
import flip from '@turf/flip'
import { along } from '../utils/map'
import CONFIG from '../config'

const maplibregl = window.maplibregl;

function crearMarkerAtleta(iniciales, color) {
  const el = document.createElement('div')
  el.className = 'atleta-marker'
  el.innerHTML = `
    <div style="position:relative">
      <div style="
        background-color:${color};color:white;font-size:12px;font-weight:bold;
        border-radius:50%;width:40px;height:40px;
        display:flex;align-items:center;justify-content:center;
        border:2px solid white;box-shadow:0 1px 4px rgba(0,0,0,0.5);
        font-family:Montserrat,sans-serif;">
        ${iniciales}
      </div>
      <div style="
        position:absolute;bottom:-6px;left:50%;transform:translateX(-50%);
        width:0;height:0;
        border-left:8px solid transparent;border-right:8px solid transparent;
        border-top:8px solid ${color};"></div>
    </div>`
  return el
}

function crearMarkerIcono(tipo) {
  const el = document.createElement('div')
  const src = tipo === 'SALIDA' ? '/img/ico-salida-v2.png'
    : tipo === 'META' ? '/img/ico-meta-v2.png'
    : '/img/ico-control-v2.png'
  el.innerHTML = `<img src="${src}" style="width:22px;height:35px" />`
  el.style.cursor = 'default'
  return el
}

const MapaFavoritos = forwardRef(function MapaFavoritos(props, ref) {
  const {
    resultados, geoJson, turfLine, turfBounds,
    sectores, controles, hideEmptyMessage
  } = props

  const containerRef = useRef(null)
  const mapRef = useRef(null)
  const atletaMarkersRef = useRef({})
  const controlMarkersRef = useRef([])
  const rutaAddedRef = useRef(false)

  // Exponer flyToAtleta al padre
  const favoritosCoords = useRef({})
  useImperativeHandle(ref, () => ({
    flyToAtleta: (dorsal) => {
      const map = mapRef.current
      const coords = favoritosCoords.current[dorsal]
      if (map && coords) {
        map.flyTo({ center: coords, zoom: 17, duration: 1500 })
      }
    }
  }))

  // Inicializar mapa
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: 'https://tiles.openfreemap.org/styles/liberty',
      center: [-8.13, 43.0],
      zoom: 13,
      pitch: 0,
      maxPitch: 85,
    })
    map.addControl(new maplibregl.NavigationControl(), 'top-right')
    map.on('load', () => {
      map.addSource('terrain-dem', {
        type: 'raster-dem',
        tiles: ['https://s3.amazonaws.com/elevation-tiles-prod/terrarium/{z}/{x}/{y}.png'],
        encoding: 'terrarium',
        tileSize: 256,
        maxzoom: 15,
      })
      map.setTerrain({ source: 'terrain-dem', exaggeration: 1.5 })
      map.addSource('ruta', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      })
      map.addLayer({
        id: 'ruta-line',
        type: 'line',
        source: 'ruta',
        paint: { 'line-color': CONFIG.PRIMARY_COLOR, 'line-width': 4 },
      })
    })
    mapRef.current = map
    return () => { map.remove(); mapRef.current = null }
  }, [])

  // Dibujar ruta y bounds
  useEffect(() => {
    const map = mapRef.current
    if (!map || !geoJson) return
    const waitForSource = () => {
      if (!map.getSource('ruta')) { setTimeout(waitForSource, 100); return }

      let features = []
      if (sectores && sectores.length && Array.isArray(geoJson)) {
        geoJson.forEach((coords, i) => {
          if (!coords || !coords.length) return
          const sector = sectores[i]
          let color = CONFIG.PRIMARY_COLOR
          if (sector && sector.mapa_geojson) {
            try {
              const parsed = JSON.parse(sector.mapa_geojson)
              const lineFeature = parsed.features.find(f => f.geometry && f.geometry.type === 'LineString')
              if (lineFeature && lineFeature.properties && lineFeature.properties.color) {
                color = lineFeature.properties.color
              }
            } catch (e) {}
          }
          features.push({
            type: 'Feature',
            properties: { color },
            geometry: { type: 'LineString', coordinates: coords.map(c => [c[1], c[0]]) }
          })
        })
      } else if (geoJson && geoJson.length) {
        // Ruta única — geoJson es array de [lat,lng]
        features.push({
          type: 'Feature',
          properties: { color: CONFIG.PRIMARY_COLOR },
          geometry: { type: 'LineString', coordinates: geoJson.map(c => [c[1], c[0]]) }
        })
      }

      if (!features.length) return

      // Actualizar source
      map.getSource('ruta').setData({ type: 'FeatureCollection', features })

      // Usar color por feature si hay sectores
      if (sectores && sectores.length) {
        map.setPaintProperty('ruta-line', 'line-color', ['get', 'color'])
      }

      // Fit bounds
      if (turfBounds && turfBounds.length === 4) {
        map.fitBounds([[turfBounds[1], turfBounds[0]], [turfBounds[3], turfBounds[2]]], {
          padding: 40, pitch: 0, animate: false
        })
      }

      // Markers salida y meta
      if (!rutaAddedRef.current) {
        rutaAddedRef.current = true
        let primerPunto, ultimoPunto
        if (sectores && sectores.length && Array.isArray(geoJson)) {
          const primerSector = geoJson[0]
          const ultimoSector = geoJson[geoJson.length - 1]
          if (primerSector && primerSector[0]) primerPunto = [primerSector[0][1], primerSector[0][0]]
          if (ultimoSector && ultimoSector.length) {
            const last = ultimoSector[ultimoSector.length - 1]
            ultimoPunto = [last[1], last[0]]
          }
        } else if (geoJson && geoJson.length) {
          primerPunto = [geoJson[0][1], geoJson[0][0]]
          const last = geoJson[geoJson.length - 1]
          ultimoPunto = [last[1], last[0]]
        }
        if (primerPunto) new maplibregl.Marker({ element: crearMarkerIcono('SALIDA'), anchor: 'bottom' }).setLngLat(primerPunto).addTo(map)
        if (ultimoPunto) new maplibregl.Marker({ element: crearMarkerIcono('META'), anchor: 'bottom' }).setLngLat(ultimoPunto).addTo(map)
      }
    }
    waitForSource()
  }, [geoJson, turfBounds, sectores])

  // Markers de controles
  useEffect(() => {
    const map = mapRef.current
    if (!map || !controles || !controles.length) return
    // Limpiar anteriores
    controlMarkersRef.current.forEach(m => m.remove())
    controlMarkersRef.current = []

    const turfLineGeoJSON = turfLine ? (Array.isArray(turfLine) ? turfLine : flip(turfLine)) : null
    const longitudRutaKm = turfLineGeoJSON
      ? (Array.isArray(turfLineGeoJSON)
        ? turfLineGeoJSON.reduce((sum, line) => sum + length(line), 0)
        : length(turfLineGeoJSON))
      : 0
    const distanciaMetaKm = controles.length > 0
      ? Math.max(...controles.map(c => c.distancia)) / 1000.0
      : longitudRutaKm
    const factorCorreccion = distanciaMetaKm > 0 ? longitudRutaKm / distanciaMetaKm : 1

    controles.forEach(control => {
      if (control.tipo === 'META' || control.tipo === 'SALIDA') return
      let lngLat = null

      if (control.lat && control.lng) {
        lngLat = [control.lng, control.lat]
      } else if (sectores && sectores.length) {
        for (let i = 0; i < sectores.length; i++) {
          const s = sectores[i]
          const dist = parseInt(control.distancia, 10)
          if (dist >= s.inicio_metros && dist <= s.fin_metros && turfLine && turfLine[i]) {
            const distEnSector = dist - s.inicio_metros
            const distEnKm = distEnSector / 1000.0
            const longitudSectorKm = s.distanciaGeo / 1000.0
            const proporcion = distEnKm / (s.distancia / 1000.0)
            const distEnRutaKm = proporcion * longitudSectorKm
            const punto = along(flip(turfLine[i]), distEnRutaKm)
            if (punto && punto.geometry && punto.geometry.coordinates) {
              const [lng, lat] = punto.geometry.coordinates
              lngLat = [lng, lat]
            }
            break
          }
        }
      } else if (turfLineGeoJSON) {
        const distAjustada = (control.distancia / 1000.0) * factorCorreccion
        const punto = along(turfLineGeoJSON, distAjustada)
        if (punto && punto.geometry && punto.geometry.coordinates) {
          const [lng, lat] = punto.geometry.coordinates
          lngLat = [lng, lat]
        }
      }

      if (lngLat) {
        const m = new maplibregl.Marker({ element: crearMarkerIcono('CONTROL'), anchor: 'bottom' })
          .setLngLat(lngLat).addTo(map)
        controlMarkersRef.current.push(m)
      }
    })
  }, [controles, turfLine, sectores])

  // Markers de atletas
  useEffect(() => {
    const map = mapRef.current
    if (!map) return

    const turfLineGeoJSON = turfLine ? (Array.isArray(turfLine) ? null : flip(turfLine)) : null
    const longitudRutaKm = turfLineGeoJSON ? length(turfLineGeoJSON) : 0
    const distanciaMetaKm = controles && controles.length > 0
      ? Math.max(...controles.map(c => c.distancia)) / 1000.0
      : longitudRutaKm
    const factorCorreccion = distanciaMetaKm > 0 ? longitudRutaKm / distanciaMetaKm : 1

    const colorValuesLength = CONFIG.COLORES_FAVORITOS.length
    const dorsalesActivos = new Set()

    resultados.forEach((resultado, idx) => {
      const colorValue = CONFIG.COLORES_FAVORITOS[idx % colorValuesLength]
      const isFinalizado = resultado.prevision && resultado.prevision.controlUltimo && resultado.prevision.controlUltimo.tipo === 'META'
      if (!resultado.prevision || (!resultado.prevision.tiempo && !isFinalizado)) return

      let lngLat = null

      // Finalizado sin porcentaje: ponerlo en meta
      if (isFinalizado && !resultado.prevision.porcentaje) {
        if (sectores && sectores.length) {
          const lastSector = sectores[sectores.length - 1]
          const lastGeo = geoJson && geoJson[sectores.length - 1]
          if (lastGeo) lngLat = [lastGeo[lastGeo.length - 1][1], lastGeo[lastGeo.length - 1][0]]
        } else if (geoJson && geoJson.length) {
          lngLat = [geoJson[geoJson.length - 1][1], geoJson[geoJson.length - 1][0]]
        }
      } else if (sectores && sectores.length) {
        const sectorIdx = resultado.prevision.sector_idx
        if (sectorIdx !== undefined && sectores[sectorIdx] && geoJson && geoJson[sectorIdx] && turfLine && turfLine[sectorIdx]) {
          const geo = geoJson[sectorIdx]
          if (resultado.prevision.porcentaje === 100) {
            lngLat = [geo[geo.length - 1][1], geo[geo.length - 1][0]]
          } else {
            const turfDistRel = sectores[sectorIdx].turfDistanceRelation || 1.0
            if (turfDistRel !== 0 && isFinite(turfDistRel)) {
              const ultimoControlConDatos = resultado.control_ultimo_id
                ? controles.find(c => c.id === String(resultado.control_ultimo_id))
                : null
              let distanciaParaCalculo = resultado.prevision.distancia
              if (ultimoControlConDatos) {
                const distanciaControlEnSector = ultimoControlConDatos.distancia - (sectores[sectorIdx].inicio_metros || 0)
                if (distanciaParaCalculo < distanciaControlEnSector) {
                  distanciaParaCalculo = distanciaControlEnSector
                }
              }
              const distanciaFinal = distanciaParaCalculo / 1000.0 / turfDistRel
              const lineFeature = { type: 'Feature', geometry: turfLine[sectorIdx].geometry || turfLine[sectorIdx] }
              const punto = along(flip(lineFeature), distanciaFinal)
              if (punto && punto.geometry && punto.geometry.coordinates) {
                lngLat = [punto.geometry.coordinates[0], punto.geometry.coordinates[1]]
              }
            }
          }
        }
      } else {
        if (resultado.prevision.porcentaje === 100 && geoJson && geoJson.length) {
          lngLat = [geoJson[geoJson.length - 1][1], geoJson[geoJson.length - 1][0]]
        } else if (turfLineGeoJSON) {
          const ultimoControlConDatos = resultado.control_ultimo_id
            ? controles.find(c => c.id === String(resultado.control_ultimo_id))
            : null
          let distanciaParaCalculo = resultado.prevision.distancia
          if (ultimoControlConDatos && resultado.prevision.distancia < ultimoControlConDatos.distancia) {
            distanciaParaCalculo = ultimoControlConDatos.distancia
          }
          const punto = along(turfLineGeoJSON, distanciaParaCalculo / 1000.0 * factorCorreccion)
          if (punto && punto.geometry && punto.geometry.coordinates) {
            lngLat = [punto.geometry.coordinates[0], punto.geometry.coordinates[1]]
          }
        }
      }

      if (!lngLat || isNaN(lngLat[0]) || isNaN(lngLat[1])) return

      const key = String(resultado.dorsal)
      dorsalesActivos.add(key)
      favoritosCoords.current[key] = lngLat

      const iniciales = resultado.nombre[0].toUpperCase() + (resultado.apellidos ? resultado.apellidos[0].toUpperCase() : '')

      if (atletaMarkersRef.current[key]) {
        atletaMarkersRef.current[key].setLngLat(lngLat)
      } else {
        const el = crearMarkerAtleta(iniciales, colorValue)
        const m = new maplibregl.Marker({ element: el, anchor: 'bottom' })
          .setLngLat(lngLat).addTo(map)
        atletaMarkersRef.current[key] = m
      }
    })

    // Eliminar markers de atletas que ya no están
    Object.keys(atletaMarkersRef.current).forEach(key => {
      if (!dorsalesActivos.has(key)) {
        atletaMarkersRef.current[key].remove()
        delete atletaMarkersRef.current[key]
      }
    })
  }, [resultados, geoJson, turfLine, turfBounds, sectores, controles])

  return <div>
    <div ref={containerRef} style={{ height: '400px', width: '100%' }} />
    {resultados.length === 0 && !hideEmptyMessage &&
      <div className="text-center py-4 text-gray-600 italic font-montserrat">
        Los atletas aparecerán en cuanto atraviesen algún punto de control
      </div>
    }
  </div>
})

export default MapaFavoritos
